import { useAppStore, type Density } from '../store/app-store.js';
import { spacingSemantic, type SpacingTier } from './spacing.js';

export const DENSITY_FACTORS: Record<Density, number> = {
  compact: 0.75,
  comfortable: 1,
  spacious: 1.25
};

function getStoreDensity(): Density {
  try {
    return useAppStore.getState().density;
  } catch {
    return 'comfortable';
  }
}

export interface SpacingRoleOptions {
  density?: Density;
}

function scale(value: number, density: Density): number {
  return Math.round(value * DENSITY_FACTORS[density]);
}

function resolve(tier: SpacingTier, opts: SpacingRoleOptions): number {
  return scale(spacingSemantic[tier], opts.density ?? getStoreDensity());
}

/** Uniform inset (padding around content). */
export function inset(tier: SpacingTier, opts: SpacingRoleOptions = {}): number {
  return resolve(tier, opts);
}

/** Vertical gap between stacked elements (row-gap / margin-block). */
export function stack(tier: SpacingTier, opts: SpacingRoleOptions = {}): number {
  return resolve(tier, opts);
}

/** Horizontal gap between inline elements (column-gap / margin-inline). */
export function inline(tier: SpacingTier, opts: SpacingRoleOptions = {}): number {
  return resolve(tier, opts);
}

/** Generic gap (CSS `gap` shorthand). */
export function gap(tier: SpacingTier, opts: SpacingRoleOptions = {}): number {
  return resolve(tier, opts);
}

/** Negative-margin bleed for full-width images / escapes from gutters. */
export function bleed(tier: SpacingTier, opts: SpacingRoleOptions = {}): number {
  return -resolve(tier, opts);
}

export interface SpacingByUseBundle {
  padding?: number;
  paddingX?: number;
  paddingY?: number;
  gap?: number;
}

interface SpacingByUseRecipe {
  padding?: SpacingTier;
  paddingX?: SpacingTier;
  paddingY?: SpacingTier;
  gap?: SpacingTier;
}

export type SpacingUse =
  | 'button'
  | 'iconButton'
  | 'card'
  | 'cardCompact'
  | 'modal'
  | 'dialog'
  | 'listRow'
  | 'inputField'
  | 'chip'
  | 'tooltip'
  | 'section'
  | 'form'
  | 'formGroup'
  | 'pageGutter'
  | 'header';

export const spacingByUseRecipes: Record<SpacingUse, SpacingByUseRecipe> = {
  button:     { paddingX: 'md', paddingY: 'sm' },
  iconButton: { padding: 'sm' },
  card:       { padding: 'lg' },
  cardCompact:{ padding: 'md' },
  modal:      { padding: 'xl' },
  dialog:     { padding: 'lg' },
  listRow:    { paddingY: 'sm', paddingX: 'md' },
  inputField: { paddingX: 'md', paddingY: 'sm' },
  chip:       { paddingX: 'sm', paddingY: 'xxs' },
  tooltip:    { padding: 'xs' },
  section:    { gap: 'xxl' },
  form:       { gap: 'md' },
  formGroup:  { gap: 'xs' },
  pageGutter: { paddingX: 'lg' },
  header:     { paddingX: 'lg', paddingY: 'md' }
};

export function spacingRole(use: SpacingUse, opts: SpacingRoleOptions = {}): SpacingByUseBundle {
  const density = opts.density ?? getStoreDensity();
  const recipe = spacingByUseRecipes[use];
  const out: SpacingByUseBundle = {};
  if (recipe.padding !== undefined)   out.padding   = scale(spacingSemantic[recipe.padding], density);
  if (recipe.paddingX !== undefined)  out.paddingX  = scale(spacingSemantic[recipe.paddingX], density);
  if (recipe.paddingY !== undefined)  out.paddingY  = scale(spacingSemantic[recipe.paddingY], density);
  if (recipe.gap !== undefined)       out.gap       = scale(spacingSemantic[recipe.gap], density);
  return out;
}
